import * as React from "react"
import { useCasePage } from "../../../hooks/applications/useCasePage"
import { Helmet } from "react-helmet"
import H1 from "../../atoms/typography/H1"
import HeroSection from "../../contentful/HeroSection"
import { MetadataModel } from "../../contentful/models"
import RichText from "../../molecules/RichText"
import PageContentWrapper from "../../organisms/PageContentWrapper"
import { useParams } from "react-router-dom"
import { HorizontalSpacer } from "../../atoms/spacers"
import { Col, Row } from "react-grid-system"
import Button from "../../atoms/buttons/Button"
import { ArrowBack } from "../../atoms/icons/ArrowBack"
import RelatedLinks from "../../molecules/news/RelatedLinks"
import ContactUsForm from "../../organisms/ContactUsForm"
import FormSection from "../../contentful/FormSection"
import { useContentfulLiveUpdates } from "@contentful/live-preview/react"
import { ContentfulLiveUpdates } from "../../../graphql/contentful/client"
import LoadingSpinner from "../../atoms/LoadingSpinner"

interface CasePageProps {
  slug: string
}

const Metadata: React.FC<MetadataModel & { imageUrl?: string }> = ({ title, description, type, imageUrl }) => {
  const url = window.location.href
  return (
    <Helmet>
      <meta property="og:title" content={title} />
      {imageUrl && <meta property="og:image" content={imageUrl} />}
      <meta property="og:url" content={url} />
      <meta property="og:type" content={type} />
      {description && <meta property="og:description" content={description} />}
      <title>{title} | Satcube.com</title>
    </Helmet>
  )
}

const CasePage: React.FC<CasePageProps> = ({ slug }) => {
  const { casePage: unliveCasePage, loading, error } = useCasePage(slug)
  const casePage = useContentfulLiveUpdates(unliveCasePage)

  if (error) {
    return <h1>{error?.message}</h1>
  }

  if (loading || !casePage) {
    return (
      <div>
        <HeroSection />
        <PageContentWrapper>
          <HorizontalSpacer spacing={2} />
          <Row>
            <Col xs="content">
              <Button
                title="Back to applications"
                variant="light"
                size="small"
                iconLeft={<ArrowBack />}
                link="/applications"
              />
            </Col>
          </Row>
          <HorizontalSpacer spacing={2} />
          <LoadingSpinner />
          <HorizontalSpacer spacing={2} />
        </PageContentWrapper>
      </div>
    )
  }

  const { title, hero, metadata, content } = casePage

  return (
    <div style={{ backgroundColor: "white" }}>
      <Metadata {...metadata} />
      <HeroSection {...hero} />
      <PageContentWrapper>
        <HorizontalSpacer spacing={2} />
        <Row>
          <Col xs="content">
            <Button
              title="Back to applications"
              variant="light"
              size="small"
              iconLeft={<ArrowBack />}
              link="/applications"
            />
          </Col>
        </Row>
        <HorizontalSpacer spacing={2} />
        {title && <H1 bold>{title}</H1>}
        <HorizontalSpacer spacing={2} />
      </PageContentWrapper>
      {content && <RichText rtf={content} />}
      <PageContentWrapper>
        <HorizontalSpacer spacing={1} />
        {metadata?.relatedLinks && <RelatedLinks links={metadata.relatedLinks} />}
        <HorizontalSpacer spacing={3} />
      </PageContentWrapper>
      <FormSection>
        <ContactUsForm />
      </FormSection>
    </div>
  )
}

export const CasePageWithParams = () => {
  const { slug } = useParams()

  if (!slug) {
    return <H1>No slug</H1>
  }

  return (
    <ContentfulLiveUpdates>
      <CasePage slug={slug} />
    </ContentfulLiveUpdates>
  )
}

export default CasePage
